// Getting Started - product scope, first-run strategy, and Compliance Health.

import {
  BULLET_VARIANTS,
  CHECKLIST_TONES,
  FAQ_BLOCK_TYPES,
  FAQ_SECTION_ICON_TONES,
} from 'src/services/consts/faq/content.const'

/** @type {import('src/services/helpers/faq/content.helper').FaqTopic} */
export const GETTING_STARTED_TOPIC = {
  id: 'getting-started',
  title: 'Getting Started',
  icon: 'rocket',
  color: 'brand-dark',
  fill: 'brand-extra-light',
  description:
    'Go live in the right order. See what Cookie Armor is for, then raise Compliance Health by fixing the right setup signal.',
  sections: [
    {
      title: 'Start here',
      items: [
        { articleId: 'what-is-cookie-armor', icon: 'rocket' },
        { articleId: 'set-up-your-first-banner', icon: 'list-checks' },
      ],
    },
    {
      title: 'Compliance Health',
      items: [{ articleId: 'understand-your-overview', icon: 'badge-check' }],
    },
  ],
}

/** @type {Record<string, import('src/services/helpers/faq/content.helper').FaqArticle>} */
export const GETTING_STARTED_ARTICLES = {
  'what-is-cookie-armor': {
    id: 'what-is-cookie-armor',
    title: 'What is Cookie Armor?',
    description:
      'Cookie Armor helps a site collect visitor consent, scan for cookies and other trackers, and hold classified tags until visitors choose - it is not legal advice and does not host a policy.',
    keywords: [
      'CMP',
      'consent management platform',
      'cookie banner',
      'consent management',
      'scripts not blocked',
      'third-party blocking',
      'legal advice',
      'privacy policy generator',
      'hosted privacy policy',
      'GCM',
      'prior blocking',
      'manual scan',
    ],
    lastUpdated: '2026-09-24',
    content: [
      {
        id: 'wia-video',
        type: FAQ_BLOCK_TYPES.VIDEO,
        videoId: '1223430842',
        hash: '04d6dbd994',
      },
      {
        id: 'wia-intro',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'Websites store cookies and other data on a visitor’s device.',
      },
      {
        id: 'wia-intro-2',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'Visitors need a clear choice about that storage, and you need those choices honoured on the live site. Cookie Armor is a consent management platform (CMP) built for that job.',
      },
      {
        id: 'wia-path',
        type: FAQ_BLOCK_TYPES.BULLETS,
        variant: BULLET_VARIANTS.JOURNEY,
        items: [
          {
            id: 'wia-path-banner',
            icon: 'layout-panel-top',
            title: 'Show a banner',
            text: '[[Create a banner|create-a-banner]] for a website, [[pick the regulations|choose-your-regulations]] that match your visitors, and [[install the script|install-the-script]] so visitors see the consent banner.',
          },
          {
            id: 'wia-path-scan',
            icon: 'scan-search',
            title: 'Scan and classify',
            text: '[[Scan|run-a-scan]] the site for cookies and other trackers, then [[review and classify|classify-cookies]] what the scan found. [[Unclassified trackers|unclassified-cookies]] stay allowed.',
          },
          {
            id: 'wia-path-honour',
            icon: 'shield-check',
            title: 'Honour the choice',
            text: 'For regulations that require prior consent, Cookie Armor [[holds classified scripts, iframes, and pixels|how-blocking-works]] until the visitor accepts.',
          },
        ],
      },
      {
        id: 'wia-legal-note',
        type: FAQ_BLOCK_TYPES.NOTE,
        icon: 'info',
        label: 'Not legal advice',
        text: 'Cookie Armor helps you collect and honour consent. It is not a law firm, it does not give legal advice, and it does not write or host your privacy policy.\nYou supply a **Privacy Policy URL** if you want the banner to link to a policy you already publish.',
      },
      {
        id: 'wia-how',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'How Cookie Armor helps',
        icon: 'shield-check',
        color: FAQ_SECTION_ICON_TONES.BRAND,
        blocks: [
          {
            id: 'wia-how-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Once your banner is live, Cookie Armor handles consent for you.',
          },
          {
            id: 'wia-how-grid',
            type: FAQ_BLOCK_TYPES.BULLETS,
            variant: BULLET_VARIANTS.GRID,
            items: [
              {
                id: 'wia-how-consent',
                icon: 'globe',
                title: 'Region-aware consent',
                text: 'Visitors see the right prompt based on the [[regulations you selected|choose-your-regulations]], and every choice is recorded automatically.',
              },
              {
                id: 'wia-how-enforce',
                icon: 'scan-search',
                title: 'Scan, classify, enforce',
                text: '[[Scan|run-a-scan]] your site for cookies and trackers, [[classify|classify-cookies]] what the scan finds, and Cookie Armor [[holds classified tags|how-blocking-works]] until visitors accept - no custom blocking code needed. [[Unclassified cookies|unclassified-cookies]] stay allowed.',
              },
              {
                id: 'wia-how-dev',
                icon: 'code',
                title: 'Developer controls',
                text: '[[Annotate tags|annotate-your-scripts]] the scan did not catch, call a [[JavaScript API|javascript-api]], or connect visitor choices to Google tags through [[Google Consent Mode|what-is-google-consent-mode]] (GCM). Category details live in [[Cookie categories|cookie-categories]].',
              },
            ],
          },
        ],
      },
      {
        id: 'wia-limits',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'What that does not cover',
        icon: 'circle-off',
        color: FAQ_SECTION_ICON_TONES.WARNING,
        blocks: [
          {
            id: 'wia-limits-list',
            type: FAQ_BLOCK_TYPES.BULLETS,
            items: [
              {
                id: 'wia-limits-regulations',
                text: 'Cookie Armor does not tell you which regulations you must use.',
              },
              {
                id: 'wia-limits-policy',
                text: 'It does not write, store, or host a privacy policy or cookie policy.',
              },
              {
                id: 'wia-limits-scan',
                text: 'Cookie Armor does not continuously monitor your site for new trackers. Your site is scanned automatically on the 20th of each month; start {btn:scan-now} to run one sooner. [[When you can trust a scan|run-a-scan]]',
              },
              {
                id: 'wia-limits-unreleased',
                text: '{regulation:lgpd} and {regulation:pipeda} appear as options but are **not yet available**. [[Choose your regulations|choose-your-regulations]]',
              },
            ],
          },
          {
            id: 'wia-next',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'To take a site live, follow [[Set up your first banner|set-up-your-first-banner]].',
          },
        ],
      },
    ],
  },

  'set-up-your-first-banner': {
    id: 'set-up-your-first-banner',
    title: 'Set up your first banner',
    description:
      'Create a banner, run the scan and install in parallel, classify trackers, review behaviour, and know when first-run setup is done.',
    keywords: [
      'first banner',
      'getting started',
      'go live',
      'create banner',
      'Scan website immediately',
      'checklist',
      'first-run',
      'setup done',
    ],
    lastUpdated: '2026-10-05',
    content: [
      {
        id: 'suf-intro',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'Take your first banner live in the right order. You need the website URL you want to cover.',
      },
      {
        id: 'suf-video-section',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'See a banner created',
        icon: 'file-video',
        color: FAQ_SECTION_ICON_TONES.BRAND,
        blocks: [
          {
            id: 'suf-video-lead',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'A short walkthrough of the create flow.',
          },
          {
            id: 'suf-video',
            type: FAQ_BLOCK_TYPES.VIDEO,
            videoId: '1223430840',
            hash: '1e2872adda',
          },
        ],
      },
      {
        id: 'suf-path',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Follow these steps',
        icon: 'list-todo',
        color: FAQ_SECTION_ICON_TONES.BRAND,
        blocks: [
          {
            id: 'suf-steps',
            type: FAQ_BLOCK_TYPES.STEPS,
            items: [
              {
                id: 'suf-step-create',
                title: 'Create the banner',
                text: 'Pick your domain, choose your regulations, and decide whether a scan starts right away. These values go live the moment you create. [[Create a banner|create-a-banner]]',
              },
              {
                id: 'suf-step-scan-install',
                title: 'Start the scan and install the script together',
                text: 'These two do not wait on each other. If you kept **Scan website immediately** on during create, a scan starts automatically; otherwise [[start one yourself|run-a-scan]]. In parallel, [[install the script|install-the-script]] on the live site and verify the install. Classification waits for the chained full scan to finish.',
              },
              {
                id: 'suf-step-classify',
                title: 'Classify what the scan found',
                text: 'The first scan of a new banner runs in [[preview mode|run-a-scan]] - a quicker crawl with incomplete results - then chains into a full scan. Found cookies and tracker categories are not editable until that full scan finishes. After it does, [[assign a category|classify-cookies]] to each detected tracker - anything left [[unclassified|unclassified-cookies]] stays allowed.',
              },
              {
                id: 'suf-step-behaviour',
                title: 'Review behaviour, then publish if you changed it',
                text: 'Confirm regulations, which languages the banner may be shown in, default language, **Privacy Policy URL**, and Google Consent Mode. Create-time values are already live; later edits stay draft until you publish. [[Configure banner behaviour|configure-banner-behaviour]]',
              },
              {
                id: 'suf-step-verify',
                title: 'Verify the installation',
                text: 'Confirm the script is installed on the live site. See [[Install the script|install-the-script]] for what each install status means.',
              },
            ],
          },
        ],
      },
      {
        id: 'suf-done',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'When first-run is done',
        icon: 'party-popper',
        color: FAQ_SECTION_ICON_TONES.POSITIVE,
        blocks: [
          {
            id: 'suf-done-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Setup is done when all of these are true: a banner exists for the domain, the script is installed, a full scan has finished and trackers are classified or you are actively classifying that full result, and behaviour has been reviewed.',
          },
          {
            id: 'suf-done-next',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Use [[Improve Compliance Health|understand-your-overview]] to decide which remaining setup signal to fix first. If the banner records no or zero events after visitors should have seen it, [[Diagnose missing events|diagnose-missing-events]].',
          },
        ],
      },
    ],
  },

  'understand-your-overview': {
    id: 'understand-your-overview',
    title: 'Improve Compliance Health',
    description:
      'Compliance Health is setup guidance - not legal compliance. Use it to see which signal to fix first and how to recover a stale or failed scan.',
    keywords: [
      'Compliance Health',
      'health score',
      'Healthy',
      'Needs Attention',
      'Needs Work',
      'stale scan',
      'setup score',
      'verified zero',
      'failed scan recovery',
    ],
    lastUpdated: '2026-09-24',
    content: [
      {
        id: 'uyo-intro',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: "Use **Compliance Health** when a banner's status looks lower than you'd expect from the setup you've already done, or when you need to decide what to fix next.",
      },
      {
        id: 'uyo-levels',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'Health reports as {status:healthy}, {status:needs-attention}, or {status:needs-work}.',
      },
      {
        id: 'uyo-health-legal',
        type: FAQ_BLOCK_TYPES.NOTE,
        icon: 'info',
        label: 'Not legal advice',
        text: 'Compliance Health is an internal setup score for your banner. It does not evaluate the banner against GDPR, CCPA, or any other regulation - a strong score is not a sign of legal compliance.',
      },
      {
        id: 'uyo-video-section',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'See how Health prioritizes work',
        icon: 'file-video',
        color: FAQ_SECTION_ICON_TONES.BRAND,
        blocks: [
          {
            id: 'uyo-video-lead',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'A short walkthrough of using Compliance Health to prioritize work.',
          },
          {
            id: 'uyo-video',
            type: FAQ_BLOCK_TYPES.VIDEO,
            videoId: '1223430841',
            hash: '6f081841d4',
          },
        ],
      },
      {
        id: 'uyo-signals',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'The signals Health watches',
        icon: 'radar',
        color: FAQ_SECTION_ICON_TONES.CYAN,
        blocks: [
          {
            id: 'uyo-signals-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Health looks at four setup signals. The Compliance Health panel on your banner calls out the ones that still need work.',
          },
          {
            id: 'uyo-signals-bullets',
            type: FAQ_BLOCK_TYPES.BULLETS,
            variant: BULLET_VARIANTS.GRID,
            items: [
              {
                id: 'uyo-signals-classify',
                icon: 'list-checks',
                title: 'Classify cookies',
                text: 'Satisfied when every detected tracker has a category - [[unclassified|unclassified-cookies]] trackers stay allowed. [[Assign a category|classify-cookies]]',
              },
              {
                id: 'uyo-signals-install',
                icon: 'code',
                title: 'Install banner',
                text: 'Satisfied when install status is {status:script-installed}. [[Install the script|install-the-script]]',
              },
              {
                id: 'uyo-signals-scan',
                icon: 'scan-search',
                title: 'Run cookies scan',
                text: 'Satisfied when the latest **full** scan is complete, fresh, and did not fail. A preview or in-progress scan does not count. [[When you can trust a scan|run-a-scan]]',
              },
              {
                id: 'uyo-signals-privacy',
                icon: 'file-text',
                title: 'Add privacy policy',
                text: "Satisfied when **Privacy Policy URL** has any value. The URL itself isn't validated. [[Configure banner behaviour|configure-banner-behaviour]]",
              },
            ],
          },
        ],
      },
      {
        id: 'uyo-recover',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Recover a stale or failed scan',
        icon: 'refresh-cw',
        color: FAQ_SECTION_ICON_TONES.WARNING,
        blocks: [
          {
            id: 'uyo-recover-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'The **Run cookies scan** signal only counts the last completed **full** scan. A preview, or a full scan still running, does not satisfy it - even if tracker rows have already appeared.',
          },
          {
            id: 'uyo-recover-p2',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'A completed full scan satisfies this signal. If that result later goes stale or a new scan fails, the signal shows up again - start the next scan yourself.',
          },
          {
            id: 'uyo-recover-bullets',
            type: FAQ_BLOCK_TYPES.BULLETS,
            variant: BULLET_VARIANTS.CHECKLIST,
            tone: CHECKLIST_TONES.NEGATIVE,
            title: "If Run cookies scan isn't satisfied",
            items: [
              {
                id: 'uyo-recover-failed',
                text: '**Latest scan failed** - the current tracker list may be out of date. Match the raw error, fix the cause, then start {btn:scan-now}. [[Fix a failed scan|fix-a-failed-scan]]',
              },
              {
                id: 'uyo-recover-stale',
                text: '**Last full scan is stale** - start a new scan so classification and Health work from a current tracker list.',
              },
              {
                id: 'uyo-recover-preview',
                text: "**Only a preview has run, or the full scan hasn't finished** - wait for the chained full scan to complete before treating classification as done.",
              },
            ],
          },
        ],
      },
      {
        id: 'uyo-priority',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'What to fix first',
        icon: 'wrench',
        color: FAQ_SECTION_ICON_TONES.WARNING,
        blocks: [
          {
            id: 'uyo-priority-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'These can be fixed in any order, with one exception - classification needs a completed full scan first. When more than one signal needs work, the order below is a good default.',
          },
          {
            id: 'uyo-priority-bullets',
            type: FAQ_BLOCK_TYPES.STEPS,
            items: [
              {
                id: 'uyo-priority-install',
                title: 'Install the script',
                text: 'Highest impact: visitors see no banner at all until this is {status:script-installed}.',
              },
              {
                id: 'uyo-priority-scan',
                title: 'Finish a full scan',
                text: 'Required before classification. Even a scan that finds zero trackers satisfies classification.',
              },
              {
                id: 'uyo-priority-classify',
                title: 'Classify remaining trackers',
                text: 'Classify as soon as the full scan finishes.',
              },
              {
                id: 'uyo-priority-privacy',
                title: 'Add a Privacy Policy URL',
                text: 'Independent of the other signals - add it at any time. Edits made after create stay draft until you {btn:publish-changes}.',
              },
            ],
          },
        ],
      },
    ],
  },
}
